import React from 'react'

import {Box} from '@mui/material'

import { SettingsSection } from 'components/display'
import { SegmentedChoice, type SegmentedChoiceOption } from 'components/input'
import { TEXT_ZOOM_LABELS, TEXT_ZOOM_LEVELS, useTextZoomFactor, type TextZoomLevel } from 'hooks'

/**
 * Text size — a front-end for the app's existing text zoom
 * (`hooks/useTextZoom`), which pre-dates this epic and already did the hard
 * part: `body { zoom }` scales text, spacing, controls and chrome together,
 * which is the "like a browser zoom" behaviour the copy promises.
 *
 * This card adds only what that hook lacked — a way to discover and set the
 * thing without knowing an undocumented keyboard chord. It writes the same
 * factor to the same localStorage key, so the Cmd +/- chords keep working and
 * the two stay in sync (the hook now emits a change event so this picker
 * re-highlights when the chords move the zoom under it).
 *
 * The chords keep their full 0.7–1.8 range; these four are shortcuts into it.
 * If the user has chorded to a factor that is not one of the four, NO option is
 * highlighted — which is honest, since none of them is what is applied.
 *
 * The option glyph is a live "Aa" at roughly the size that option produces, so
 * the control previews its own effect rather than needing four arbitrary icons.
 * `lineHeight: 1` keeps all four buttons the same height despite the differing
 * glyph sizes.
 */

/** Preview glyph size per level. Relative hint, not a measurement. */
const PREVIEW_FONT_SIZE: Record<TextZoomLevel, number> = {
  0.9: 11,
  1: 13,
  1.2: 15,
  1.4: 17,
}

const ZOOM_OPTIONS: readonly SegmentedChoiceOption<TextZoomLevel>[] = TEXT_ZOOM_LEVELS.map(
  (level) => ({
    value: level,
    label: TEXT_ZOOM_LABELS[level],
    icon: (
      <Box
        component="span"
        aria-hidden
        sx={{ fontSize: PREVIEW_FONT_SIZE[level], fontWeight: 600, lineHeight: 1 }}
      >
        Aa
      </Box>
    ),
  }),
)

export const TextSizeCard = () => {
  const { factor, setFactor } = useTextZoomFactor()

  return (
    <SettingsSection
      title="Text size"
      description="Find the text small or hard to read? Make everything bigger here. Scales the whole interface (text, spacing, and controls) larger or smaller, and applies on this browser only. Your browser's own zoom still works too, with Cmd + and Cmd -."
    >
      <SegmentedChoice
        ariaLabel="Interface scale"
        options={ZOOM_OPTIONS}
        value={factor as TextZoomLevel}
        onChange={setFactor}
      />
    </SettingsSection>
  )
}
